import React, { useState, useRef, useContext } from 'react';
import { Modal } from '@vimily/bonjoro-ui-components-v2/Modal';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import Tabs from './Components/Tabs';
import Content from './Components/Content';
import SelectController from './Components/SelectController';
import { usePublishSelectModalContext } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishSelectModalContext';
import { useUpdatePublishable } from '~/App/Api/Publishable/api';
import { useQueryClient } from 'react-query';
import { queryKeys } from '~/App/Api/Publishable/api';
import FlashContext from '~/App/Components/Flash/Context';
import { AxiosError } from 'axios';
import { PublishableType } from '~/types/models/publishable';
import { ModalTabs } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Containers/PublishModal/types';
import { MAX_TESTIMONIALS } from '../../../constants';
import { usePublishEditorState } from '../../Contexts/PublishEditorContext';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  TESTIMONIAL_PUBLISH_SELECT_TESTIMONIALS_CANCEL,
  TESTIMONIAL_PUBLISH_SELECT_TESTIMONIALS_SAVE,
} from '~/generated/AnalyticsTypes';

export interface PublishModalProps {
  uuid: string;
  open: boolean;
  closeModal: () => void;
}

export interface ErrorResponseData {
  error: { message: string };
}

export default function PublishModal({
  uuid,
  open,
  closeModal,
}: PublishModalProps) {
  const [selectedTab, setSelectedTab] = useState(ModalTabs.All);
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const { createFlash } = useContext(FlashContext);

  const {
    removeAllSelectedIds,
    selectedIds,
    isDirty,
    done,
    resetSelectedAllIdsToDefault,
  } = usePublishSelectModalContext()!;

  const [{ type }] = usePublishEditorState();

  const targetRef = useRef<HTMLDivElement>(null);

  const handleCancel = () => {
    resetSelectedAllIdsToDefault();
    closeModal();

    analytics.track(TESTIMONIAL_PUBLISH_SELECT_TESTIMONIALS_CANCEL, {
      publishType: type,
    });
  };

  const { mutateAsync: updatePublishableMutation, isLoading: updating } =
    useUpdatePublishable({
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.all);
        removeAllSelectedIds(ModalTabs.All);
        done();
        closeModal();
      },
      onError: (error: AxiosError) => {
        createFlash({
          id: 'update-testimonial-error',
          title: 'Oops!',
          message:
            (error as AxiosError<ErrorResponseData>)?.response?.data?.error
              ?.message ??
            'Something wrong happened while updating publishable',
          options: { style: 'danger' },
        });
      },
      useErrorBoundary: false,
    });

  const saveSelectedIds = () => {
    updatePublishableMutation({
      type,
      uuid,
      publishable_resources_uuids: selectedIds,
      get_publishable_resources: true,
    });
  };

  const handleClickSave = () => {
    saveSelectedIds();
    resetSelectedAllIdsToDefault();

    analytics.track(TESTIMONIAL_PUBLISH_SELECT_TESTIMONIALS_SAVE, {
      publishType: type,
    });
  };

  return (
    <Modal
      open={open}
      hideCloseButton={true}
      className='v2-max-w-[1160px] rounded-lg v2-h-[800px]'
    >
      <div className='v2-flex v2-flex-col p-0'>
        <div className='flex items-center justify-between mb-[30px]'>
          <h3 className='v2-text-[28px] v2-font-bold'>
            Select testimonials &nbsp;
            {type === PublishableType.Slider &&
              `(${selectedIds.length}/${MAX_TESTIMONIALS})`}
            {type === PublishableType.WallOfLove && `(${selectedIds.length})`}
          </h3>
          <div>
            <Button
              variant={ButtonVariant.Secondary}
              size='md'
              bgColorClassName={'v2-bg-transparent'}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              size='md'
              className='m-[10px]'
              onClick={handleClickSave}
              disabled={updating || !isDirty}
            >
              Save
            </Button>
          </div>
        </div>
        <div>
          <Tabs onSelectedTab={setSelectedTab} selectedTab={selectedTab} />
          <SelectController selectedTab={selectedTab} />
        </div>
        <div
          id='publish-modal'
          className='overflow-y-auto v2-max-h-[550px] mt-2.5'
          ref={targetRef}
        >
          <Content scrollContainerRef={targetRef} selectedTab={selectedTab} />
        </div>
      </div>
    </Modal>
  );
}
